import { Button, Checkbox, Group, LoadingOverlay, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import type { JSX } from "react";
import { useMutation } from "urql";
import {
    type AskAiMutation,
    cloneMutation,
    type TuneThreadCloneMutation,
    type TuneThreadCloneMutationVariables,
} from "./queries";

interface IProps {
    tuneThread: {
        id: number;
        label: string | null;
    };
    disabled: boolean;
    testItems: AskAiMutation["tuneThreadAskAi"];
}

function CloneForm({
    initialLabel,
    hasTestItems,
    fetching,
    onSubmit,
}: {
    initialLabel: string;
    hasTestItems: boolean;
    fetching: boolean;
    onSubmit: (label: string, includeTestItems: boolean) => void;
}): JSX.Element {
    const form = useForm<{ label: string; includeTestItems: boolean }>({
        initialValues: { label: initialLabel, includeTestItems: false },
    });

    return (
        <form onSubmit={form.onSubmit(({ label, includeTestItems }) => onSubmit(label, includeTestItems))}>
            <Stack maw={600} mx="auto">
                <LoadingOverlay visible={fetching} />
                <TextInput label="Label" placeholder="Enter a label (optional)" {...form.getInputProps("label")} />
                <Checkbox
                    mt="sm"
                    label="Include result items from latest test"
                    disabled={!hasTestItems}
                    {...form.getInputProps("includeTestItems", { type: "checkbox" })}
                />
                <Group justify="flex-end" mt="md">
                    <Button type="submit" disabled={fetching}>
                        Clone
                    </Button>
                </Group>
            </Stack>
        </form>
    );
}

function CloneThreadButton({ tuneThread, disabled, testItems }: IProps): JSX.Element {
    const [cloneResult, executeClone] = useMutation<TuneThreadCloneMutation, TuneThreadCloneMutationVariables>(
        cloneMutation,
    );

    const handleClone = async (newLabel: string, includeTestItems: boolean) => {
        const result = await executeClone({
            data: {
                tuneThreadId: tuneThread.id,
                label: newLabel.trim() === "" ? null : newLabel,
                appendItems:
                    includeTestItems && testItems
                        ? testItems.map((item) => ({
                              type: item.type,
                              role: item.role,
                              content: item.content,
                              toolName: item.toolName,
                              toolArguments: item.toolArguments,
                              toolCallId: item.toolCallId,
                              reasoningId: item.reasoningId,
                              encryptedReasoning: item.encryptedReasoning,
                              reasoningSummary: item.reasoningSummary,
                              context: false,
                          }))
                        : [],
            },
        });
        if (result.error) {
            notifications.show({ title: "Failed to clone thread", message: result.error.message });
        } else {
            notifications.show({ title: "Thread cloned", message: "Thread cloned" });
        }
    };

    const onClick = () =>
        modals.open({
            title: "Clone thread",
            size: "600px",
            children: (
                <CloneForm
                    fetching={cloneResult.fetching}
                    hasTestItems={testItems.length > 0}
                    initialLabel={`${tuneThread.label ?? "Untitled"} (copy)`}
                    onSubmit={(label, include) => {
                        handleClone(label, include);
                        modals.closeAll();
                    }}
                />
            ),
        });

    return (
        <Button onClick={onClick} disabled={cloneResult.fetching || disabled}>
            Clone
        </Button>
    );
}

export default CloneThreadButton;
